import * as React from "react"

import { cn } from "@/lib/utils"
import { type Tone, toneClasses } from "@/lib/tone"

/** Use instead of a hand-written `bg-X-100 text-X-700` triplet; lib/tone.ts decides the look. */
export interface StatusPillProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  tone: Tone
  label: string
  withDot?: boolean
  size?: "sm" | "md"
}

const SIZES = {
  sm: "text-[10px] px-1.5 py-0 gap-1",
  md: "text-xs px-2 py-0.5 gap-1.5",
} as const

export function StatusPill({
  tone,
  label,
  withDot = false,
  size = "md",
  className,
  ...props
}: StatusPillProps) {
  const t = toneClasses(tone)
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border font-medium",
        SIZES[size],
        t.badge,
        className,
      )}
      {...props}
    >
      {withDot && (
        <span
          className={cn("h-1.5 w-1.5 flex-none rounded-full", t.dot)}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
  )
}

/** Always pair with a text label in the row — colour is never the only carrier of the state. */
export function StatusDot({
  tone,
  className,
  ...props
}: { tone: Tone } & React.HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn("inline-block h-2.5 w-2.5 flex-none rounded-full", toneClasses(tone).dot, className)}
      aria-hidden="true"
      {...props}
    />
  )
}
